import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useT } from "@/i18n";
import { fileUrl } from "@/lib/api";
import { playCrystal } from "@/lib/sound";

const PARTICLES = Array.from({ length: 22 });

export const CreatureReveal = ({ creature, region, onClose }) => {
  const { t } = useT();
  const [phase, setPhase] = useState("summon"); // summon -> burst -> reveal
  const color = creature?.color || "#D4AF37";
  const img = creature?.image_path ? fileUrl(creature.image_path) : creature?.image_url || null;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("burst"), 1400);
    const t2 = setTimeout(() => {
      setPhase("reveal");
      playCrystal();
    }, 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => phase === "reveal" && onClose()}
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-[#050814]/95 backdrop-blur-xl"
      data-testid="creature-reveal"
    >
      <div className="relative flex h-64 w-64 items-center justify-center">
        {/* glow */}
        <motion.div
          className="absolute inset-0 rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${color}88, transparent 70%)` }}
          animate={{ scale: phase === "summon" ? [1, 1.15, 1] : 1.4, opacity: phase === "reveal" ? 0.8 : 0.5 }}
          transition={{ duration: 1.4, repeat: phase === "summon" ? Infinity : 0 }}
        />

        {/* orb / egg */}
        <AnimatePresence>
          {phase !== "reveal" && (
            <motion.div
              key="orb"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{
                scale: phase === "burst" ? [1, 1.3, 0] : [1, 1.06, 1],
                opacity: phase === "burst" ? [1, 1, 0] : 1,
                rotate: phase === "summon" ? [0, 6, -6, 0] : 0,
              }}
              transition={{ duration: phase === "burst" ? 0.6 : 2, repeat: phase === "summon" ? Infinity : 0 }}
              className="relative flex h-28 w-28 items-center justify-center rounded-full"
              style={{
                background: `radial-gradient(circle at 35% 30%, ${color}, #0A0F1D)`,
                boxShadow: `0 0 50px ${color}aa, inset 0 0 30px ${color}55`,
              }}
            >
              <Sparkles className="h-10 w-10 text-white/90" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* burst particles */}
        {phase !== "summon" &&
          PARTICLES.map((_, i) => {
            const angle = (i / PARTICLES.length) * Math.PI * 2;
            const dist = 120 + (i % 3) * 20;
            return (
              <motion.span
                key={i}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{
                  x: Math.cos(angle) * dist,
                  y: Math.sin(angle) * dist,
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0.4],
                }}
                transition={{ duration: 1.2, delay: 0.05 * (i % 5) }}
                className="absolute h-1.5 w-1.5 rounded-full"
                style={{ background: color, boxShadow: `0 0 8px ${color}` }}
              />
            );
          })}

        {/* revealed creature */}
        <AnimatePresence>
          {phase === "reveal" && (
            <motion.div
              key="creature"
              initial={{ scale: 0.4, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              className={`relative flex items-center justify-center overflow-hidden rounded-full ${img ? "h-44 w-44" : "h-32 w-32"}`}
              style={{
                background: `radial-gradient(circle at 35% 30%, ${color}55, #0A0F1D)`,
                boxShadow: `0 0 60px ${color}, inset 0 0 24px ${color}77`,
                border: `2px solid ${color}`,
              }}
            >
              {img ? (
                <img src={img} alt={creature?.name} className="h-full w-full object-cover" data-testid="reveal-image" />
              ) : (
                <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 2.4, repeat: Infinity }}>
                  <Sparkles className="h-14 w-14" style={{ color }} />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-6 h-24 text-center">
        {phase !== "reveal" ? (
          <p className="animate-pulse font-serif text-lg text-[#D4AF37]">{t("reveal.summon")}</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p className="text-xs tracking-[0.3em] text-[#A3B1C6]/70">{region}</p>
            <h3 className="mt-1 font-serif text-3xl font-semibold" style={{ color }} data-testid="reveal-name">
              {creature?.name} {t("reveal.appeared")}
            </h3>
            <p className="mx-auto mt-2 max-w-xs text-sm text-[#A3B1C6]">{creature?.description}</p>
            <p className="mt-4 text-xs text-[#A3B1C6]/50">{t("reveal.tap")}</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
